import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getWhatsAppBridgeQr, getWhatsAppBridgeStatus, connectWhatsAppBridge, disconnectWhatsAppBridge } from '../api/client';

interface WhatsAppState {
  status: 'disconnected' | 'connecting' | 'qr_ready' | 'connected';
  phone?: string;
  qr?: string;
  apiReachable?: boolean;
}

interface WhatsAppConnectProps {
  compact?: boolean;
  onStatusChange?: (status: WhatsAppState['status']) => void;
}

export default function WhatsAppConnect({ compact = false, onStatusChange }: WhatsAppConnectProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<WhatsAppState>({ status: 'disconnected' });
  const [bridgeOnline, setBridgeOnline] = useState(true);
  const [fullWipe, setFullWipe] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const failCountRef = useRef(0);

  useEffect(() => { onStatusChange?.(state.status); }, [state.status, onStatusChange]);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await getWhatsAppBridgeQr();
      setBridgeOnline(true);
      failCountRef.current = 0;
      setState({
        status: data.status,
        phone: data.phone,
        qr: data.qr || undefined,
        apiReachable: data.apiReachable,
      });
    } catch {
      // Try the simpler status endpoint as fallback
      try {
        const data = await getWhatsAppBridgeStatus();
        setBridgeOnline(true);
        failCountRef.current = 0;
        setState(prev => ({
          status: data.status,
          phone: data.phone,
          qr: data.status === 'qr_ready' ? prev.qr : undefined,
        }));
        return;
      } catch { /* fall through */ }

      failCountRef.current += 1;
      if (failCountRef.current === 1) {
        console.warn('WA bridge unreachable — pausing poll');
      }
      setBridgeOnline(false);
      setState({ status: 'disconnected' });

      // Back off polling: stop after 3 consecutive failures
      if (failCountRef.current >= 3 && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchStatus]);

  const connect = async (fresh = false) => {
    setState({ status: 'connecting' });
    try {
      await connectWhatsAppBridge(fresh);
      failCountRef.current = 0;
      if (!pollRef.current) {
        pollRef.current = setInterval(fetchStatus, 2000);
      }
      setTimeout(fetchStatus, 2000);
    } catch (err) {
      console.warn('WA connect failed:', err);
      setState({ status: 'disconnected' });
    }
  };

  const disconnect = async () => {
    try {
      await disconnectWhatsAppBridge(fullWipe);
    } catch {
      /* ignore */
    }
    setState({ status: 'disconnected' });
    setFullWipe(false);
  };

  if (!bridgeOnline) {
    if (compact) {
      return (
        <div className="flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-700">
          <span>⚠️</span>
          <span>{t('whatsapp.bridgeOffline')}</span>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-yellow-200 bg-yellow-50 p-6 text-center shadow-sm">
        <span className="mb-3 block text-3xl">⚠️</span>
        <p className="text-sm text-yellow-700">{t('whatsapp.bridgeOffline')}</p>
      </div>
    );
  }

  // Compact mode for banners / settings
  if (compact) {
    if (state.status === 'connected') {
      return (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2.5">
            <div className="flex items-center gap-2">
              <span className="flex h-2.5 w-2.5 rounded-full bg-green-500">
                <span className="inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
              </span>
              <span className="text-sm font-medium text-green-800">
                {t('whatsapp.connected')}
              </span>
              {state.phone && (
                <span className="text-sm text-green-600">({state.phone})</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-[10px] text-gray-500 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={fullWipe} 
                  onChange={e => setFullWipe(e.target.checked)}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500 h-3 w-3"
                />
                {t('whatsapp.fullWipe')}
              </label>
              <button
                onClick={disconnect}
                className="rounded-md px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
              >
                {t('whatsapp.disconnect')}
              </button>
            </div>
          </div>
          {state.apiReachable === false && (
            <div className="flex items-center gap-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-100 px-2 py-1 rounded">
              <span>⚠️ {t('whatsapp.apiUnreachable')}</span>
            </div>
          )}
        </div>
      );
    }

    // Show QR inline in compact mode when ready
    if (state.status === 'qr_ready' && state.qr) {
      return (
        <div className="rounded-lg border border-green-200 bg-white px-4 py-3">
          <div className="flex items-start gap-4">
            <div className="shrink-0 rounded border border-gray-100 p-1">
              <img src={state.qr} alt="WhatsApp QR" className="h-40 w-40" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{t('whatsapp.scanQR')}</p>
              <p className="mt-1 text-xs text-gray-500">
                {t('whatsapp.instructions.step1')}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Connecting state
    if (state.status === 'connecting' || state.status === 'qr_ready') {
      return (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-green-300 border-t-[#25D366]" />
            <span className="text-sm text-yellow-700">{t('whatsapp.connecting')}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-gray-400" />
          <span className="text-sm text-gray-600">{t('whatsapp.disconnected')}</span>
        </div>
        <button
          onClick={() => connect()}
          className="rounded-md bg-[#25D366] px-3 py-1 text-xs font-medium text-white hover:bg-[#1da851] disabled:opacity-50 transition-colors"
        >
          {t('whatsapp.connect')}
        </button>
      </div>
    );
  }

  // Full mode
  const currentStatus = state.status;
  return (
    <div className="mx-auto max-w-2xl">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {/* Header */}
        <div className="bg-[#25D366] px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📱</span>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {t('whatsapp.title')}
              </h2>
              <p className="text-sm text-green-100">{t('whatsapp.subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Disconnected */}
          {currentStatus === 'disconnected' && (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <span className="text-3xl">📵</span>
              </div>
              <p className="mb-6 max-w-sm text-sm text-gray-500">
                {t('whatsapp.subtitle')}
              </p>
              <button
                onClick={() => connect()}
                className="rounded-lg bg-[#25D366] px-8 py-3 text-sm font-semibold text-white shadow-md transition-all hover:bg-[#1da851] hover:shadow-lg active:scale-[0.98]"
              >
                {t('whatsapp.connect')}
              </button>
            </div>
          )}

          {/* Connecting */}
          {currentStatus === 'connecting' && (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="relative mb-4">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-green-200 border-t-[#25D366]" />
              </div>
              <p className="text-sm font-medium text-gray-700">
                {t('whatsapp.connecting')}
              </p>
              <p className="mt-1 text-xs text-gray-400">
                {t('whatsapp.waitingForQR')}
              </p>
            </div>
          )}

          {/* QR Ready */}
          {currentStatus === 'qr_ready' && (
            <div className="flex flex-col items-center gap-6 md:flex-row md:items-start">
              {/* QR Code */}
              <div className="shrink-0">
                {state.qr ? (
                  <div className="rounded-lg border-2 border-gray-100 p-2">
                    <img
                      src={state.qr}
                      alt="WhatsApp QR Code"
                      className="h-[264px] w-[264px]"
                    />
                  </div>
                ) : (
                  <div className="flex h-[280px] w-[280px] items-center justify-center rounded-lg border-2 border-dashed border-gray-200">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-green-200 border-t-[#25D366]" />
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="flex-1">
                <h3 className="mb-4 text-base font-semibold text-gray-900">
                  {t('whatsapp.scanQR')}
                </h3>
                <ol className="space-y-3 text-sm text-gray-600">
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-xs font-bold text-white">
                      1
                    </span>
                    <span>{t('whatsapp.instructions.step1')}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-xs font-bold text-white">
                      2
                    </span>
                    <span>{t('whatsapp.instructions.step2')}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-xs font-bold text-white">
                      3
                    </span>
                    <span>{t('whatsapp.instructions.step3')}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-xs font-bold text-white">
                      4
                    </span>
                    <span>{t('whatsapp.instructions.step4')}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-xs font-bold text-white">
                      5
                    </span>
                    <span>{t('whatsapp.instructions.step5')}</span>
                  </li>
                </ol>
              </div>
            </div>
          )}

          {/* Connected */}
          {currentStatus === 'connected' && (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <span className="text-3xl">✅</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {t('whatsapp.connected')}
              </p>
              {state.phone && (
                <p className="mt-1 text-sm text-gray-500">
                  {t('whatsapp.connectedTo')} <span className="font-mono font-medium text-gray-700">{state.phone}</span>
                </p>
              )}
              <p className="mt-1 text-xs text-green-600">{t('whatsapp.sessionActive')}</p>
              
              {state.apiReachable === false && (
                <div className="mt-4 rounded-md bg-amber-50 p-3 border border-amber-200">
                  <div className="flex gap-2 items-center text-amber-800 text-sm font-medium">
                    <span>⚠️ {t('whatsapp.bridgeToApiFailed')}</span>
                  </div>
                  <p className="mt-1 text-xs text-amber-700">
                    {t('whatsapp.bridgeToApiDesc')}
                  </p>
                </div>
              )}

              <div className="mt-8 flex flex-col items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={fullWipe} 
                    onChange={e => setFullWipe(e.target.checked)}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span>{t('whatsapp.fullWipeDesc')}</span>
                </label>
                <button
                  onClick={disconnect}
                  className="rounded-lg border border-red-200 px-8 py-2.5 text-sm font-medium text-red-600 transition-all hover:bg-red-50 active:scale-[0.98]"
                >
                  {t('whatsapp.disconnect')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
